// The portal on the Lender Hub API (LH-B1, firstfund-lender-hub/api2). Since LH-B4 always on (vite.config.ts sets
// VITE_HUB_API=1 in every mode but the tests'): the dev server proxies /admin/v1 to :3009, so the session cookie and
// the CSRF check stay first-party. Off only in the tests (vitest): the screens run on the fixtures.
// Routes and shapes: planner/lender-hub/api-contract.md §1, firstfund-contracts/schemas/hub/.
import type { Alert, HistoryEntry, Lender, LenderStatusChanged, PortalSettings, User } from '@/types'

let on = ['1', 'true'].includes(String(import.meta.env.VITE_HUB_API ?? '').toLowerCase())
/** Is the portal on the API? */
export const apiMode = () => on
/** for the tests */
export const setApiMode = (v: boolean) => { on = v }

const BASE = String(import.meta.env.VITE_HUB_API_BASE ?? '')

export class ApiError extends Error {
  constructor(public status: number, public error: string, message: string, public extra: Record<string, unknown> = {}) {
    super(message)
    this.name = 'ApiError'
  }
}

let csrf = ''
export const setCsrf = (token: string) => { csrf = token }

/** One call to the API: JSON in and out, the cookie sent, the CSRF token on every change. */
export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${BASE}/admin/v1${path}`, {
      method,
      credentials: 'include',
      headers: { ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(method !== 'GET' ? { 'x-csrf-token': csrf } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
  } catch {
    throw new ApiError(0, 'offline', 'The Lender Hub API is not reachable. In firstfund-lender-hub/api2 run: npm run dev')
  }
  const text = await res.text()
  const data = text ? (JSON.parse(text) as Record<string, unknown>) : {}
  if (!res.ok) {
    const { error, message, ...extra } = data as { error?: string; message?: string }
    throw new ApiError(res.status, error ?? 'error', message ?? `${method} ${path}: ${res.status}`, extra)
  }
  if (typeof data.csrf_token === 'string') csrf = data.csrf_token
  return data as T
}

// ---- sign-in ----------------------------------------------------------------------------------------------------

export type Stage = 'mfa' | 'change_password' | 'mfa_setup' | 'ok'
export interface SessionView {
  stage: Stage
  csrf_token: string
  two_step_rule: 'optional' | 'required' | 'off'
  user: { id: string; email: string; name: string; role: User['role']; two_step: boolean }
  permissions: string[]
  recovery_codes?: string[]
  recovery_codes_left?: number
}

export const auth = {
  session: () => api<SessionView>('GET', '/auth/session'),
  signIn: (email: string, password: string) => api<SessionView>('POST', '/auth/sign-in', { email, password }),
  verify: (input: { code?: string; recovery_code?: string }) => api<SessionView>('POST', '/auth/2-step/verify', input),
  changePassword: (current: string, next: string) => api<SessionView>('POST', '/auth/password', { current_password: current, new_password: next }),
  setUpTwoStep: () => api<{ secret: string; otpauth_uri: string; qr_svg?: string }>('POST', '/auth/2-step/setup'),
  confirmTwoStep: (code: string) => api<SessionView>('POST', '/auth/2-step/confirm', { code }),
  signOut: () => api<void>('POST', '/auth/sign-out'),
}

// ---- the records ----------------------------------------------------------------------------------------------

type Envelope = { data: LenderStatusChanged }

export const hub = {
  lenders: () => api<Lender[]>('GET', '/lenders'),
  history: (limit = 500) => api<HistoryEntry[]>('GET', `/history?limit=${limit}`),
  alerts: () => api<Alert[]>('GET', '/alerts'),
  users: () => api<User[]>('GET', '/users'),
  events: async () => (await api<Envelope[]>('GET', '/events')).map((e) => e.data),
  settings: () => api<PortalSettings>('GET', '/settings'),
}
